import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation } from '@tanstack/react-query';

import { whatsAppService } from '../../application/WhatsAppService';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';

interface UseCreateWhatsAppSenderProps {
  subaccountName: string;
  phone: string;
  wabaId: string;
  name: string;
  address?: string;
  logoUrl?: string;
  description?: string;
  about?: string;
  emails?: string[];
  websites?: string[];
}

type UseCreateWhatsAppSenderResponse = ServiceResponse<
  ({
    subaccountName,
    phone,
    wabaId,
    name,
    address,
    logoUrl,
    description,
    about,
    emails,
    websites,
  }: UseCreateWhatsAppSenderProps) => void,
  void
>;

// Hooks act as 'Adapter' layer.
export const useCreateWhatsAppSender = (): UseCreateWhatsAppSenderResponse => {
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();

  const { isLoading, isSuccess, isError, mutate } = useMutation(
    ({
      subaccountName,
      phone,
      wabaId,
      name,
      address,
      logoUrl,
      description,
      about,
      emails,
      websites,
    }: UseCreateWhatsAppSenderProps): Promise<void> => {
      return whatsAppService.createWhatsAppSender({
        subaccountName,
        phone,
        wabaId,
        name,
        address,
        logoUrl,
        description,
        about,
        emails,
        websites,
      });
    },
    {
      onSuccess: () => {
        showSnackbarMessage({
          type: `success`,
          messageIntlId: `register_whatsapp.process_successful`,
          autoHideDuration: 10000,
        });
      },
      onError: (err: any) => {
        console.info(`Create WhatsApp Sender User Error:`, err);
        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    run: mutate,
    error: isError,
    success: isSuccess,
    isLoading: isLoading,
  };
};
